import { useMindmapNodesForRoot } from "frontend/subscriptions";
import { atom, useAtomValue } from "jotai";
import { selectAtom } from "jotai/utils";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MindmapNodeDirection, MindmapNodeElement, MindmapNodeOrientation } from "shared/datamodel/schemas/mindmap";
import {
  editingElementIdAtom,
  editingElementLinkAtom,
  selectedElementIdsAtom,
  syncServiceAtom,
  utilsAtom,
} from "state-atoms";
import { Circle, Group, Rect, Text, Line } from "react-konva";
import MindmapNodeCanvasElement from "./elements/mindmap-node-element";
import { Point, viewportToStage } from "frontend/utils/math-utils";
import { Degrees } from "frontend/utils/transform";
import { DropTarget, calculateDropTarget } from "./elements/mindmap/mindmap-drag-utils";
import { KonvaEventObject } from "konva/types/Node";
import {
  buildNodesTree,
  calculateNodeConnectorAnchor,
  calculateParentConnectorAnchor,
  findNode,
  getNodesWithPositions,
  MindmapNodeTree,
} from "frontend/utils/mindmap-utils";
import consts from "shared/consts";
import { CanvasArrow } from "./utility-elements";
import { getFontSize } from "./text-element";
import { useCurrentCanvasValue } from "./canvas-context";
import tracking from "frontend/tracking";
import { MindmapNewNodeButton } from "./utility-elements/mindmap-sockets";
import SimpleConnector from "./elements/connector/simple-connector";
import type Konva from "konva";
import { ITraits, Trait } from "./elements-toolbar/elements-toolbar-types";
import { equals } from "rambda";
import { LockType } from "shared/datamodel/schemas";

export default function MindmapCanvasElement({
  id,
  element,
  changeElement,
  allElementsData,
}: {
  id: string;
  element: MindmapNodeElement;
  allElementsData?: Array<[string, any]>;
  changeElement: (
    props: any,
    undoConfig: { shouldAdd: boolean; previousProps?: any },
    updateSubMenuData?: any, //take the last change and make it the default when selecting this element again from the menu (e.g. make the default color the last color selected)
    elementId?: string
  ) => void;
}) {
  const syncService = useAtomValue(syncServiceAtom);
  const editingElementLinkId = useAtomValue(editingElementLinkAtom);
  const locked = (element.lock ?? LockType.None) != LockType.None;

  const { nodes: allNodes, nodesSizes } = useMindmapNodesForRoot(
    syncService?.getReplicache(),
    element.rootId,
    allElementsData
  );

  // Create a memoized set of all mindmap node IDs for this mindmap (reused for multiple derived atoms)
  const mindmapNodeIds = useMemo(() => {
    const ids = new Set<string>([element.rootId]);
    Object.keys(allNodes).forEach((nodeId) => ids.add(nodeId));
    return ids;
  }, [element.rootId, allNodes]);

  // Create a derived atom that filters selectedElementIdsAtom to only include mindmap nodes
  // This prevents re-renders when non-mindmap elements are selected/deselected
  // selectAtom uses the equality function to only trigger updates when the filtered result actually changes
  const selectedMindmapNodeIdsAtom = useMemo(
    () =>
      selectAtom(
        selectedElementIdsAtom,
        (allSelectedIds) => allSelectedIds.filter((id) => mindmapNodeIds.has(id)),
        equals
      ),
    [mindmapNodeIds]
  );

  // Create a derived atom that filters editingElementIdAtom to only return the ID if it's a mindmap node
  // This prevents re-renders when non-mindmap elements enter/exit editing mode
  const editingMindmapNodeIdAtom = useMemo(
    () =>
      atom((get) => {
        const editingId = get(editingElementIdAtom);
        return editingId && mindmapNodeIds.has(editingId) ? editingId : null;
      }),
    [mindmapNodeIds]
  );

  const selectedMindmapNodeIds = useAtomValue(selectedMindmapNodeIdsAtom);
  const editingElementId = useAtomValue(editingMindmapNodeIdAtom);

  const [nodeSizeById, setNodeSizeById] = useState<{ [id: string]: { width: number; height: number } }>(nodesSizes);

  const [{ documentId }] = useCurrentCanvasValue();
  const canvasUtils = useAtomValue(utilsAtom(documentId));

  const groupRef = useRef<Konva.Group | null>(null);
  const [dropMarker, setDropMarker] = useState<DropTarget | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Drag context to track drag operation with document event handlers
  const dragCtx = useRef<{
    draggedNodeIds: Set<string>;
    descendantIds: string[];
    descendantNodes: Konva.Node[];
    descendantConnectors: Konva.Node[];
    dragRectangles: Konva.Node[]; // Invisible drag rectangles that need position restoration
    originalPositions: Map<string, Point>;
    initialMousePos: Point;
    dirty: boolean;
    currentDropMarker: DropTarget | null; // Store current drop marker for immediate access in onMouseUp
    eventHandlers: {
      onMouseMove: (e: MouseEvent) => void;
      onMouseUp: (e: MouseEvent) => void;
      onKeyDown: (e: KeyboardEvent) => void;
    };
  } | null>(null);

  // map all nodes to their children for easy access
  const childrenMap = Object.entries(allNodes).reduce((acc, [id, node]) => {
    if (!node.parentId) {
      return acc;
    }
    if (!acc[node.parentId]) {
      acc[node.parentId] = [];
    }
    acc[node.parentId].push(id);
    return acc;
  }, {} as { [parentId: string]: string[] });

  // build the tree of nodes
  const nodesTree = buildNodesTree({ rootId: id, root: element, childrenMap, allNodes });

  // calculate the positions of all nodes
  const layout = getNodesWithPositions(nodesTree, nodeSizeById);

  // Memoize descendant map for O(1) lookup instead of O(n²) recursive calls
  const descendantMap = useMemo(() => {
    const map = new Map<string, string[]>();

    const buildDescendantMap = (node: MindmapNodeTree): string[] => {
      if (map.has(node.id)) return map.get(node.id)!;

      const descendants: string[] = [];
      if (node.children) {
        for (const child of node.children) {
          descendants.push(child.id);
          descendants.push(...buildDescendantMap(child));
        }
      }
      map.set(node.id, descendants);
      return descendants;
    };

    if (layout) {
      buildDescendantMap(layout);
    }
    return map;
  }, [layout]);

  useEffect(() => {
    setNodeSizeById(nodesSizes);
  }, [nodesSizes]);

  // Cleanup event listeners on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (dragCtx.current) {
        cleanupDrag(true);
      }
    };
  }, []);

  // Cleanup on tab switch/visibility change to prevent zombie listeners
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && dragCtx.current) {
        cleanupDrag();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  function onDragStart(e: KonvaEventObject<DragEvent>) {
    e.cancelBubble = true;
    e.evt.preventDefault();

    // Defensive checks
    if (locked) {
      console.warn("Cannot drag: mindmap is locked");
      return;
    }
    if (!canvasUtils) {
      console.warn("Cannot drag: canvasUtils not available");
      return;
    }

    // Initialize dragCtx if null
    if (dragCtx.current === null) {
      initializeDragContext(e);
    }

    if (!dragCtx.current) return;

    // Determine which nodes to drag
    let nodesToDrag: string[] = [];

    if (selectedMindmapNodeIds.length > 0) {
      // Use selected nodes if any are selected
      nodesToDrag = selectedMindmapNodeIds.filter((id) => id !== element.rootId);
    } else {
      // Otherwise, use the node from the event
      const nodeId = e.target.attrs?.["data-node-id"];
      if (nodeId && nodeId !== element.rootId) {
        nodesToDrag = [nodeId];
      }
    }

    if (nodesToDrag.length === 0) return;

    // Add all nodes to the drag context
    for (const nodeId of nodesToDrag) {
      dragCtx.current.draggedNodeIds.add(nodeId);
    }
    dragCtx.current.dirty = true;
  }

  function initializeDragContext(e: KonvaEventObject<DragEvent>) {
    const stage = e.target.getStage();
    if (!stage) return;

    const initialMousePos = {
      x: e.evt.clientX,
      y: e.evt.clientY,
    };

    const onMouseMove = (ev: MouseEvent) => {
      // Check if mouse buttons are still pressed
      if (ev.buttons === 0) {
        cleanupDrag();
        return;
      }

      if (!dragCtx.current) return;

      // Recompute if dirty
      if (dragCtx.current.dirty) {
        recomputeDragContext();
        dragCtx.current.dirty = false;
      }

      // Calculate delta from initial position
      let dx = ev.clientX - dragCtx.current.initialMousePos.x;
      let dy = ev.clientY - dragCtx.current.initialMousePos.y;

      // Convert from screen to canvas coordinates
      dx /= stage.scale().x;
      dy /= stage.scale().y;
      dx /= element.scaleX ?? 1;
      dy /= element.scaleY ?? 1;

      const offset = { x: dx, y: dy };

      // Apply offset to dragged nodes
      for (const node of dragCtx.current.descendantNodes) {
        const originalPos = dragCtx.current.originalPositions.get(node.id());
        if (originalPos) {
          node.position({
            x: originalPos.x + offset.x,
            y: originalPos.y + offset.y,
          });
        }
      }

      // Apply offset to drag rectangles
      for (const rect of dragCtx.current.dragRectangles) {
        const rectNodeId = rect.attrs["data-node-id"];
        if (rectNodeId) {
          const originalPos = dragCtx.current.originalPositions.get(`rect-${rectNodeId}`);
          if (originalPos) {
            rect.position({
              x: originalPos.x + offset.x,
              y: originalPos.y + offset.y,
            });
          }
        }
      }

      // Apply offset to connectors
      for (const connector of dragCtx.current.descendantConnectors) {
        connector.offset({ x: -offset.x, y: -offset.y });
      }

      // Calculate drop target
      const canvasPos = viewportToStage(
        { x: stage.position().x, y: stage.position().y, scale: stage.scale().x },
        { x: ev.clientX, y: ev.clientY }
      );

      const { scaleX = 1, scaleY = 1 } = element;
      const localPos = {
        x: (canvasPos.x - element.x) / scaleX,
        y: (canvasPos.y - element.y) / scaleY,
      };

      // Get the first dragged node for drop calculation
      const firstDraggedNodeId = Array.from(dragCtx.current.draggedNodeIds)[0];

      // Get current state of the first dragged node to filter out no-op drops
      const draggedNode = findNode(nodesTree, firstDraggedNodeId);
      let currentParentId: string | undefined;
      let currentDirection: MindmapNodeDirection | undefined;
      let currentPrevSiblingId: string | undefined;
      let currentNextSiblingId: string | undefined;

      if (draggedNode) {
        currentParentId = draggedNode.parentId ?? undefined;
        currentDirection = draggedNode.direction;

        // Find current siblings to determine position
        if (draggedNode.parentId !== undefined && draggedNode.parentId !== null) {
          const parent = findNode(nodesTree, draggedNode.parentId);
          if (parent?.children) {
            const siblings = parent.children.filter(
              (child) => child.direction === draggedNode.direction && child.id !== draggedNode.id
            );
            const currentIndex = siblings.findIndex((child) => child.sortIndex > draggedNode.sortIndex);

            if (currentIndex === -1) {
              // This node is the last child
              currentPrevSiblingId = siblings[siblings.length - 1]?.id;
            } else if (currentIndex === 0) {
              // This node is the first child
              currentNextSiblingId = siblings[0]?.id;
            } else {
              // This node is somewhere in the middle
              currentPrevSiblingId = siblings[currentIndex - 1]?.id;
              currentNextSiblingId = siblings[currentIndex]?.id;
            }
          }
        }
      }

      const dropTarget = calculateDropTarget(
        localPos,
        layout,
        firstDraggedNodeId,
        dragCtx.current.descendantIds,
        currentParentId,
        currentDirection,
        currentPrevSiblingId,
        currentNextSiblingId
      );

      // Store in ref for immediate access in onMouseUp (avoids async state update race condition)
      dragCtx.current.currentDropMarker = dropTarget;
      // Also update state for rendering
      setDropMarker(dropTarget);

      stage.batchDraw();
    };

    const onMouseUp = (_ev: MouseEvent) => {
      if (!dragCtx.current) return;

      // Perform drop if there's a valid target (use ref to get current value, avoiding async state race)
      const currentDropMarker = dragCtx.current.currentDropMarker;
      if (currentDropMarker) {
        // Find all top-level dragged nodes (nodes whose parents are not in the dragged set)
        const topLevelDraggedNodeIds: string[] = [];
        for (const nodeId of dragCtx.current.draggedNodeIds) {
          const node = findNode(nodesTree, nodeId);
          if (node) {
            // A node is top-level if its parent is not in the dragged set
            const parentIsDragged = node.parentId && dragCtx.current.draggedNodeIds.has(node.parentId);
            if (!parentIsDragged) {
              topLevelDraggedNodeIds.push(nodeId);
            }
          }
        }

        // Check if we should perform the drop for the first top-level node
        if (topLevelDraggedNodeIds.length > 0) {
          const firstNodeId = topLevelDraggedNodeIds[0];
          const firstNode = findNode(nodesTree, firstNodeId);
          if (!firstNode) {
            console.warn("Cannot drop: dragged node not found:", firstNodeId);
          } else {
            // Check if this is a no-op drop (dropping in the same location)
            let isNoOpDrop = false;

            if (currentDropMarker.type === "child" && currentDropMarker.targetNode) {
              // Dropping as child on the same parent with same direction
              const originalParentId = firstNode.parentId;
              const dropOnSameParent = currentDropMarker.targetNode.id === originalParentId;
              const sameDirection =
                currentDropMarker.direction === (firstNode?.direction ?? MindmapNodeDirection.TRAILING);
              isNoOpDrop = dropOnSameParent && sameDirection;
            }
            // For "between" drops, we allow the drop even if it might be the same position
            // The fractional ordering will handle it gracefully

            if (!isNoOpDrop) {
              // Drop all top-level nodes
              handleDropMutation(topLevelDraggedNodeIds, currentDropMarker);
            }
          }
        }
      }

      cleanupDrag();
    };

    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        cleanupDrag();
      }
    };

    dragCtx.current = {
      draggedNodeIds: new Set(),
      descendantIds: [],
      descendantNodes: [],
      descendantConnectors: [],
      dragRectangles: [],
      originalPositions: new Map(),
      initialMousePos,
      dirty: true,
      currentDropMarker: null,
      eventHandlers: {
        onMouseMove,
        onMouseUp,
        onKeyDown,
      },
    };

    // Attach event listeners
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("keydown", onKeyDown);
  }

  function recomputeDragContext() {
    if (!dragCtx.current) return;

    const nodesGroup = groupRef.current;
    if (!nodesGroup) return;

    // Clear previous computed data
    dragCtx.current.descendantIds = [];
    dragCtx.current.descendantNodes = [];
    dragCtx.current.descendantConnectors = [];
    dragCtx.current.dragRectangles = [];
    dragCtx.current.originalPositions.clear();

    // Collect all descendants of dragged nodes (using memoized map for O(1) lookup)
    const allDescendantIds = new Set<string>();
    for (const nodeId of dragCtx.current.draggedNodeIds) {
      const node = findNode(nodesTree, nodeId);
      if (!node) {
        console.warn("Dragged node not found during recompute:", nodeId);
        continue;
      }
      const descendants = descendantMap.get(nodeId) || [];
      descendants.forEach((id) => allDescendantIds.add(id));
    }
    dragCtx.current.descendantIds = Array.from(allDescendantIds);

    // Collect Konva nodes
    nodesGroup.getChildren().each((child: Konva.Node) => {
      if (child.attrs.type == consts.CANVAS_ELEMENTS.MINDMAP) {
        const childId = child.id();
        if (childId && (dragCtx.current!.draggedNodeIds.has(childId) || allDescendantIds.has(childId))) {
          dragCtx.current!.descendantNodes.push(child);
          dragCtx.current!.originalPositions.set(childId, { ...child.position() });
          // child.moveToTop();
        }
      }
    });

    // Collect connectors
    // For each node, if its parent is also being dragged, collect the connector
    for (const nodeId of [...dragCtx.current.draggedNodeIds, ...allDescendantIds]) {
      const node = findNode(nodesTree, nodeId);
      if (!node || !node.parentId) continue;

      const parentIsDragged = dragCtx.current.draggedNodeIds.has(node.parentId) || allDescendantIds.has(node.parentId);

      const connectorName = `mindmap-connector-${node.parentId}-${nodeId}`;
      const connector = nodesGroup.findOne((n: Konva.Node) => n.name() === connectorName);

      if (connector) {
        if (parentIsDragged) {
          // Parent is also being dragged, so move connector with the nodes
          dragCtx.current.descendantConnectors.push(connector);
          // connector.moveToTop();
        } else {
          // Parent is not being dragged, hide the connector
          connector.visible(false);
        }
      }
    }
  }

  function cleanupDrag(isUnmounting: boolean = false) {
    if (!dragCtx.current) return;

    // Remove event listeners
    document.removeEventListener("mousemove", dragCtx.current.eventHandlers.onMouseMove);
    document.removeEventListener("mouseup", dragCtx.current.eventHandlers.onMouseUp);
    document.removeEventListener("keydown", dragCtx.current.eventHandlers.onKeyDown);

    // Clear context and state
    dragCtx.current = null;

    if (!isUnmounting) {
      setDropMarker(null);
      setRefreshKey((prev) => prev + 1);
    }
  }

  function handleDropMutation(draggedNodeIds: string[], target: DropTarget) {
    if (!canvasUtils) {
      console.warn("Cannot perform drop: canvasUtils not available");
      return;
    }
    if (!target) {
      console.warn("Cannot perform drop: target is null");
      return;
    }

    let parentId: string;
    let baseOrder: number;
    let direction = target.direction; // Use target direction if specified
    let spacing = 1;

    if (target.type === "child") {
      // Dropping as child of target node
      if (!target.targetNode) {
        console.warn("Cannot drop: target node is undefined");
        return;
      }
      parentId = target.targetNode.id;

      // Check for circular reference
      for (const draggedId of draggedNodeIds) {
        const descendants = descendantMap.get(draggedId) || [];
        if (descendants.includes(parentId) || draggedId === parentId) {
          console.warn("Cannot drop: would create circular reference", { draggedId, parentId });
          return;
        }
      }

      const parentNode = findNode(nodesTree, parentId);
      if (!parentNode) {
        console.warn("Cannot drop: parent node not found:", parentId);
        return;
      }

      // Add to end of children
      baseOrder = parentNode.children?.length || 0;

      // If direction not specified (e.g. dropping on root), infer it
      if (direction === undefined) {
        if (parentId === element.rootId) {
          direction = MindmapNodeDirection.TRAILING; // Fallback
        } else {
          direction = parentNode.direction ?? MindmapNodeDirection.TRAILING;
        }
      }
    } else {
      // Dropping between siblings
      if (!target.parentNode) {
        console.warn("Cannot drop: parent node is undefined");
        return;
      }
      parentId = target.parentNode.id;

      // Check for circular reference
      for (const draggedId of draggedNodeIds) {
        const descendants = descendantMap.get(draggedId) || [];
        if (descendants.includes(parentId) || draggedId === parentId) {
          console.warn("Cannot drop: would create circular reference", { draggedId, parentId });
          return;
        }
      }
      // Calculate order based on siblings
      if (!target.prevSibling && target.nextSibling) {
        // Before first child
        baseOrder = target.nextSibling.sortIndex - 1 - spacing * draggedNodeIds.length;
        direction = target.nextSibling.direction;
      } else if (target.prevSibling && !target.nextSibling) {
        // After last child
        baseOrder = target.prevSibling.sortIndex + 1;
        direction = target.prevSibling.direction;
      } else if (target.prevSibling && target.nextSibling) {
        // Between two siblings
        baseOrder = (target.prevSibling.sortIndex + target.nextSibling.sortIndex) / 2;
        spacing = (target.nextSibling.sortIndex - baseOrder) / (draggedNodeIds.length + 1);
        direction = target.prevSibling.direction;
      } else {
        console.warn("Cannot drop: invalid between state");
        return;
      }
    }

    // Build the update map for all dragged nodes
    const updates = new Map();
    const previousValues = new Map();

    // Process each dragged node
    draggedNodeIds.forEach((draggedNodeId, index) => {
      // Calculate order for this node (use 1.0 spacing to avoid collisions with existing siblings)
      const order = baseOrder + index * spacing;

      // Capture previous values for undo
      const draggedNode = findNode(nodesTree, draggedNodeId);
      if (draggedNode) {
        previousValues.set(draggedNodeId, {
          parentId: draggedNode.parentId,
          direction: draggedNode.direction,
          sortIndex: draggedNode.sortIndex,
        });
      }

      updates.set(draggedNodeId, {
        parentId,
        direction,
        sortIndex: order,
      });

      // Update direction of all descendants to match the new direction (using memoized map)
      if (draggedNode) {
        const descendantIds = descendantMap.get(draggedNodeId) || [];
        for (const descendantId of descendantIds) {
          const descendantNode = findNode(nodesTree, descendantId);
          if (descendantNode) {
            // Capture previous direction for undo
            previousValues.set(descendantId, {
              direction: descendantNode.direction,
            });
          }

          updates.set(descendantId, {
            direction,
          });
        }
      }
    });

    canvasUtils.onElementsMutationEnded(updates, previousValues);
    setTimeout(() => canvasUtils.enableTransformer(), 16);
  }

  function onChangeNodeSize(id: string, size: { width: number; height: number }) {
    setNodeSizeById((prev) => ({ ...prev, [id]: size }));
  }

  function onFinishedEditingNode(cancelled: boolean, node: MindmapNodeTree) {
    if (!canvasUtils) {
      return;
    }

    if (cancelled) {
      canvasUtils.clearSelectedElementIds();
    } else {
      const parentId = node.parentId ?? node.id;
      addNewNode(parentId, node.direction ?? MindmapNodeDirection.TRAILING);
    }
  }

  function addNewNode(parentId: string, direction: MindmapNodeDirection) {
    if (!canvasUtils) {
      return;
    }
    const parent = findNode(nodesTree, parentId);
    let color = consts.DEFAULTS.MINDMAP_COLOR;
    let order = 0;
    if (parent) {
      if (parentId === element.rootId) {
        // new color for the root node
        order = parent.children?.length ?? 0;
        color = consts.MINDMAP_COLOR_PALETTE[order % consts.MINDMAP_COLOR_PALETTE.length];
      } else {
        // same color as the parent
        color = parent.color ?? consts.DEFAULTS.MINDMAP_COLOR;
        order = parent.children?.length ?? 0;
      }
    }
    canvasUtils.addMindmapNode(parentId, element.rootId, direction, order, color);
  }

  function renderConnector(
    node: Point,
    color: string,
    parent?: Point,
    isLeading: boolean = false,
    nodeId?: string,
    parentId?: string
  ) {
    if (!parent) {
      return null;
    }
    let parentRotation = element.orientation === MindmapNodeOrientation.VERTICAL ? 0 : (90 as Degrees);
    let nodeRotation = element.orientation === MindmapNodeOrientation.VERTICAL ? 180 : (270 as Degrees);
    if (isLeading) {
      // swap the rotation of the connector
      // so that the arrow is always pointing to the node from the parent
      [parentRotation, nodeRotation] = [nodeRotation, parentRotation];
    }
    return (
      <Group
        key={`connector-${parentId}-${nodeId}`}
        name={`mindmap-connector-${parentId}-${nodeId}`}
        data-parent-id={parentId}
        data-child-id={nodeId}
      >
        <SimpleConnector
          id={"mindmap-connector"}
          p1={{ ...parent, rotation: parentRotation as Degrees }}
          p2={{ ...node, rotation: nodeRotation as Degrees }}
          lineType={"curve"}
          element={{
            strokeWidth: 3,
            stroke: color,
            // innerPoints: [{ x: parent!.x, y: parent!.y, id: "ASD" }],
          }}
          curveStrength={50}
        />
      </Group>
    );
  }

  function renderAddNodeButton(node: MindmapNodeTree, isLeading: boolean) {
    if (node.collapsed || selectedMindmapNodeIds.length !== 1 || !selectedMindmapNodeIds.includes(node.id)) {
      return null;
    }

    const showingCollapseButton = node.parentId && node.descendantsCount;
    const padding = showingCollapseButton
      ? consts.DEFAULTS.MINDMAP_ADD_NODE_BUTTON_PADDING
      : consts.DEFAULTS.MINDMAP_CONNECTOR_CHILD_PADDING;
    const toAnchor = calculateParentConnectorAnchor(node, element.orientation, isLeading, padding);
    const direction = isLeading ? MindmapNodeDirection.LEADING : MindmapNodeDirection.TRAILING;
    return (
      <MindmapNewNodeButton
        position={toAnchor}
        onClick={() => {
          tracking.trackEvent(consts.TRACKING_CATEGORY.CANVAS_ACTION, "add-mindmap-node-clicked");
          addNewNode(node.id, direction);
        }}
      />
    );
  }

  function renderCollapseButton(
    node: MindmapNodeTree,
    color: string,
    orientation: MindmapNodeOrientation,
    isLeading: boolean
  ) {
    if (!node.parentId || (!node.children?.length && !node.collapsed)) {
      return null;
    }
    const shouldShowCollapseButton = node.collapsed || selectedMindmapNodeIds.includes(node.id);
    const fromAnchor = calculateParentConnectorAnchor(node, element.orientation, isLeading);
    const toAnchor = calculateParentConnectorAnchor(
      node,
      element.orientation,
      isLeading,
      consts.DEFAULTS.MINDMAP_CONNECTOR_CHILD_PADDING
    );
    const rotation = calculateButtonRotation(orientation, isLeading);
    return (
      <>
        {node.collapsed && renderConnector(toAnchor, color, fromAnchor, isLeading)}
        {shouldShowCollapseButton && (
          <CollapseButton
            position={toAnchor}
            collapsed={node.collapsed}
            color={color}
            onClick={() => {
              tracking.trackEvent(
                consts.TRACKING_CATEGORY.CANVAS_ACTION,
                "mindmap_collapse_clicked",
                node.collapsed ? "expand" : "collapse",
                node.descendantsCount
              );
              changeElement({ collapsed: !node.collapsed }, { shouldAdd: true }, undefined, node.id);
            }}
            text={node.collapsed ? `${node.descendantsCount}` : "-"}
            rotation={rotation}
          />
        )}
      </>
    );
  }

  function renderTreeNode(node: MindmapNodeTree, color: string, parentAnchor?: Point): React.ReactNode {
    const size = nodeSizeById[node.id];
    if (!size) {
      return null;
    }
    // we calculate both anchors from this node to its children
    // because the root node can have children on both sides
    // on other nodes we'll only use one of them
    const parentToTrailingNodeAnchor = calculateParentConnectorAnchor(node, element.orientation, false);
    const parentToLeadingNodeAnchor = calculateParentConnectorAnchor(node, element.orientation, true);
    const toChildAnchor = calculateNodeConnectorAnchor(node, element.orientation);
    const isLeading = node.direction === MindmapNodeDirection.LEADING;
    const renderLeadingAddButton = !locked && (!node.parentId || isLeading);
    const renderTrailingAddButton = !locked && (!node.parentId || !isLeading);

    let hasLeadingSibling = false;

    return (
      <React.Fragment key={node.id}>
        {renderConnector(
          toChildAnchor,
          node.color ?? color,
          parentAnchor,
          isLeading,
          node.id,
          node.parentId ?? undefined
        )}
        {!node.collapsed &&
          node.children.map((child) => {
            const isLeading = child.direction === MindmapNodeDirection.LEADING;
            const anchor = isLeading ? parentToLeadingNodeAnchor : parentToTrailingNodeAnchor;
            hasLeadingSibling = hasLeadingSibling || isLeading;
            return renderTreeNode(child, node.color ?? color, anchor);
          })}
        {!isLeading && renderCollapseButton(node, node.color ?? color, element.orientation, false)}
        {isLeading && renderCollapseButton(node, node.color ?? color, element.orientation, true)}
        {renderLeadingAddButton && renderAddNodeButton(node, true)}
        {renderTrailingAddButton && renderAddNodeButton(node, false)}
        <MemoizedMindmapNode
          id={node.id}
          node={node}
          absolutePosition={{ x: element.x + node.x, y: element.y + node.y }}
          size={size}
          changeElement={changeElement}
          isEditing={editingElementId === node.id}
          isEditingLink={editingElementLinkId === node.id}
          childrenCount={node.children?.length ?? 0}
          onChangeSize={(size) => onChangeNodeSize(node.id, size)}
          onFinishedEditing={(cancelled) => onFinishedEditingNode(cancelled, node)}
        />
      </React.Fragment>
    );
  }

  function renderLayout() {
    if (!layout) {
      return null;
    }
    return renderTreeNode(layout, element.color ?? consts.DEFAULTS.MINDMAP_COLOR);
  }

  function collectAllNodes(node: MindmapNodeTree): MindmapNodeTree[] {
    const nodes: MindmapNodeTree[] = [node];
    if (!node.collapsed && node.children) {
      for (const child of node.children) {
        nodes.push(...collectAllNodes(child));
      }
    }
    return nodes;
  }

  function renderDragRectangles() {
    if (!layout) {
      return null;
    }

    const allNodes = collectAllNodes(layout);

    return allNodes.map((node) => {
      const size = nodeSizeById[node.id];
      if (!size || node.id === element.rootId) {
        // Skip root node as it's handled by canvas-stage
        return null;
      }

      return (
        <Rect
          key={`drag-rect-${node.id}`}
          // id={node.id} // don't set this - it will make the node discoverable in canvas-stage
          x={node.x}
          y={node.y}
          width={size.width}
          height={size.height}
          isCanvasElement={false}
          isSelectable={false}
          fill="transparent"
          opacity={0}
          listening={true}
          draggable={true}
          data-node-id={node.id}
          onDragStart={onDragStart}
        />
      );
    });
  }

  function renderSelectedNodesBoundingRect() {
    // Only render when more than 1 node is selected
    if (selectedMindmapNodeIds.length <= 1 || !layout) {
      return null;
    }

    // Collect all nodes from layout
    const allNodes = collectAllNodes(layout);

    // Find selected nodes and calculate bounding box
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const nodeId of selectedMindmapNodeIds) {
      const node = allNodes.find((n) => n.id === nodeId);
      const size = nodeSizeById[nodeId];

      if (node && size && nodeId !== element.rootId) {
        minX = Math.min(minX, node.x);
        minY = Math.min(minY, node.y);
        maxX = Math.max(maxX, node.x + size.width);
        maxY = Math.max(maxY, node.y + size.height);
      }
    }

    // If no valid nodes found, don't render
    if (minX === Infinity) {
      return null;
    }

    return (
      <Rect
        key="selected-nodes-bounding-rect"
        x={minX}
        y={minY}
        width={maxX - minX}
        height={maxY - minY}
        isCanvasElement={false}
        isSelectable={false}
        fill="transparent"
        opacity={0}
        listening={true}
        draggable={true}
        onDragStart={onDragStart}
      />
    );
  }

  return (
    <Group key={refreshKey} x={element.x} y={element.y} ref={groupRef}>
      {renderLayout()}
      {!locked && renderDragRectangles()}
      {!locked && renderSelectedNodesBoundingRect()}
      {!locked && dropMarker && (
        <>
          {dropMarker.type === "child" && dropMarker.targetNode ? (
            <Rect
              x={dropMarker.x - dropMarker.targetNode.width / 2}
              y={dropMarker.y - dropMarker.targetNode.height / 2}
              width={dropMarker.targetNode.width}
              height={dropMarker.targetNode.height}
              stroke="#0091ff"
              strokeWidth={2}
              cornerRadius={5}
              fill="rgba(0, 145, 255, 0.1)"
              listening={false}
            />
          ) : dropMarker.type === "between" ? (
            (() => {
              // Calculate line points based on orientation
              const isVertical = element.orientation === MindmapNodeOrientation.HORIZONTAL; // remember it's confusing in mindmap - horizontal means vertical and vice versa !!!
              const lineLength = 30;
              const points = isVertical
                ? [dropMarker.x, dropMarker.y - lineLength, dropMarker.x, dropMarker.y + lineLength]
                : [dropMarker.x - lineLength, dropMarker.y, dropMarker.x + lineLength, dropMarker.y];

              return <Line points={points} stroke="#0091ff" strokeWidth={2} listening={false} />;
            })()
          ) : null}
        </>
      )}
    </Group>
  );
}

function CollapseButton({
  position,
  collapsed,
  color,
  text,
  rotation,
  onClick,
}: {
  position: Point;
  collapsed: boolean;
  color: string;
  text: string;
  rotation: number;
  onClick: () => void;
}) {
  const [hover, setHover] = useState(false);

  const circleRef = useRef<Konva.Group | null>(null);
  const rectRef = useRef<Konva.Rect | null>(null);
  const textRef = useRef<Konva.Text | null>(null);

  const CircleRadius = 8;
  const CirclePadding = 4;

  useEffect(() => {
    circleRef.current?.to({
      scaleX: hover ? 1.2 : 1,
      scaleY: hover ? 1.2 : 1,
      duration: 0.1,
    });
  }, [hover]);

  useEffect(() => {
    if (!textRef.current || !rectRef.current) {
      return;
    }
    const textWidth = textRef.current.getWidth();
    const rectWidth = Math.max(textWidth + CirclePadding * 2, CircleRadius * 2);
    textRef.current.x(-textWidth / 2);
    rectRef.current.x(-rectWidth / 2);
    rectRef.current.width(rectWidth);
    const layer = textRef.current.getLayer();
    if (layer) {
      layer.batchDraw();
    }
  }, [text, collapsed]);

  const mouseHandlingProps = {
    name: "mindmap-collapse anchor",
    onClick,
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
  };

  return (
    <Group x={position.x} y={position.y} ref={circleRef} rotation={rotation}>
      {!collapsed && (
        <>
          <Circle
            radius={CircleRadius}
            fill={collapsed ? color : "white"}
            stroke={color}
            strokeWidth={2}
            {...mouseHandlingProps}
          />
          <Group x={2} y={0}>
            <CanvasArrow
              pointerLength={3}
              pointerWidth={3}
              strokeWidth={2}
              stroke={color}
              fill={color}
              listening={false}
            />
          </Group>
        </>
      )}
      {collapsed && (
        <Group rotation={-rotation}>
          <Rect
            ref={rectRef}
            y={-CircleRadius}
            height={CircleRadius * 2}
            cornerRadius={CircleRadius}
            fill={color}
            {...mouseHandlingProps}
          />
          <Text
            ref={textRef}
            y={CirclePadding - CircleRadius}
            text={text}
            align="center"
            verticalAlign="middle"
            fontFamily="Poppins"
            fontSize={10}
            // fontStyle="bold"
            fill="white"
            letterSpacing={1}
            listening={false}
          />
        </Group>
      )}
    </Group>
  );
}

export function mindmapTraits(element: MindmapNodeElement): ITraits {
  const defaultColor = element.parentId ? consts.DEFAULTS.MINDMAP_COLOR : consts.DEFAULTS.MINDMAP_ROOT_STROKE_COLOR;
  const baseTraits = {
    mindmapColor: element.color ?? defaultColor,
    textColor: element.textColor ?? consts.DEFAULTS.TEXT_COLOR,
    fontProps: element.fontProps ?? 0,
    fontSize: getFontSize(element),
    font: element.font ?? consts.DEFAULTS.FONT,
  };
  if (element.parentId) {
    return baseTraits;
  }
  // root node
  return {
    ...baseTraits,
    mindmapOrientation: element.orientation,
  };
}

export function mindmapValidateTrait(_element: MindmapNodeElement, _trait: Trait, value: any) {
  return value;
}

function calculateButtonRotation(orientation: MindmapNodeOrientation, isLeading: boolean): Degrees {
  if (orientation === MindmapNodeOrientation.VERTICAL) {
    if (isLeading) {
      return 0 as Degrees;
    } else {
      return 180 as Degrees;
    }
  } else if (isLeading) {
    return 90 as Degrees;
  } else {
    return 270 as Degrees;
  }
}

const MemoizedMindmapNode = React.memo(MindmapNodeCanvasElement);
